import React from 'react';
import PropTypes from 'prop-types';
import { 
  // Navigation & UI
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Plus, Minus, X, Check, Search, Filter, Settings, Bell,
  Calendar, Clock, User, Users, Grid3X3, List, ClipboardList,
  Sparkles, Home, LogOut, MessageCircle, Tent, GripVertical, MoreHorizontal, PanelLeft, Menu,
  Zap, Rocket, Quote, MessageSquare, Contact, Gift, Brain, Atom, Activity, LoaderCircle,
  
  // Weather
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog,
  
  // Location & Maps
  MapPin, Map, Navigation,
  
  // Actions & Status
  Edit, Trash2, Save, Download, Upload, Share, Copy, Eye, EyeOff,
  AlertTriangle, AlertCircle, CheckCircle, XCircle, Info,
  HelpCircle, ExternalLink, ArrowRight, ArrowLeft, TrendingUp,
  RotateCcw, LockKeyhole, Shield, Eraser, Ruler, Bookmark, LifeBuoy,
  
  // Communication
  Phone, Mail, Send,
  
  // Media & Files
  Image, File, Folder, FolderOpen, FileText, FileImage,
  
  // Social & Family
  Heart, Star, ThumbsUp, ThumbsDown, Baby, Briefcase, UserPlus, UserCheck, UserX, CircleUser,
  
  // Finance
  DollarSign, PiggyBank,
  
  // Activities & Categories
  BookOpen, Palette, Music, Camera, Gamepad2, Dumbbell,
  TreePine, Mountain, Waves, Car, Plane, Train,
  GraduationCap, School, Lightbulb,
  
  // System & Tech
  Wifi, WifiOff, Battery, BatteryLow, Volume2, VolumeX,
  Monitor, Smartphone, Tablet, Laptop, Headphones, Link as LinkIcon,
  
  // Default fallback
  Circle
} from 'lucide-react';

const GoogleIcon = ({ className = '', ...props }) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    {...props}
  >
    <path fill="#4285F4" d="M21.35 11.1h-9.17v2.98h5.27c-.23 1.26-.92 2.33-1.96 3.05v2.52h3.17c1.86-1.71 2.92-4.23 2.92-7.19 0-.66-.06-1.3-.23-1.94z" />
    <path fill="#34A853" d="M12.18 22c2.64 0 4.86-.87 6.47-2.36l-3.17-2.52c-.9.6-2.05.97-3.3.97-2.55 0-4.71-1.72-5.47-4.04H3.43v2.57C5.03 19.98 8.35 22 12.18 22z" />
    <path fill="#FBBC04" d="M6.71 13.4c-.2-.6-.32-1.24-.32-1.9 0-.66.12-1.3.32-1.9V7.03H3.43A9.88 9.88 0 0 0 2.18 11.5c0 1.56.36 3.04 1.25 4.47l3.28-2.57z" />
    <path fill="#EA4335" d="M12.18 6.5c1.45 0 2.75.5 3.77 1.48l2.83-2.83C16.98 3.6 14.76 2.7 12.18 2.7 8.35 2.7 5.03 4.72 3.43 7.97l3.28 2.57c.76-2.32 2.92-4.04 5.47-4.04z" />
  </svg>
);

/**
 * Unified Icon Component
 * 
 * Provides a consistent interface for all icons across the app.
 * Supports Lucide React icons, custom icons, and special cases.
 * 
 * @param {string} name - Icon name (Lucide React icon name or custom)
 * @param {string} size - Icon size: 'xs', 'sm', 'md', 'lg', 'xl', '2xl'
 * @param {string} className - Additional CSS classes
 * @param {string} color - Icon color (Tailwind color class)
 * @param {boolean} showTooltip - Show tooltip on hover
 * @param {string} tooltip - Custom tooltip text
 * @param {object} props - Additional props passed to the icon component
 */
const Icon = ({ 
  name, 
  size = 'md', 
  className = '', 
  color = '',
  showTooltip = false,
  tooltip,
  ...props 
}) => {
  // Size mapping
  const sizeClasses = {
    'xs': 'h-3 w-3',   // 12px
    'sm': 'h-4 w-4',   // 16px
    'md': 'h-5 w-5',   // 20px
    'lg': 'h-6 w-6',   // 24px
    'xl': 'h-8 w-8',   // 32px
    '2xl': 'h-10 w-10' // 40px
  };

  // Color mapping
  const colorClasses = {
    'primary': 'text-blue-600',
    'secondary': 'text-gray-600',
    'success': 'text-green-600',
    'warning': 'text-yellow-600',
    'error': 'text-red-600',
    'info': 'text-blue-500',
    'muted': 'text-gray-400',
    'slate': 'text-slate-600',
    'white': 'text-white',
    'black': 'text-black'
  };

  // Icon mapping - comprehensive list of all icons used in the app
  const iconMap = {
    // Navigation & UI
    'chevron-left': ChevronLeft,
    'chevron-right': ChevronRight,
    'chevron-down': ChevronDown,
    'chevron-up': ChevronUp,
    'plus': Plus,
    'minus': Minus,
    'x': X,
    'close': X,
    'check': Check,
    'search': Search,
    'filter': Filter,
    'settings': Settings,
    'bell': Bell,
    'calendar': Calendar,
    'clock': Clock,
    'user': User,
    'users': Users,
    'grid': Grid3X3,
    'list': List,
    'clipboard': ClipboardList,
    'sparkles': Sparkles,
    'home': Home,
    'logout': LogOut,
    'tent': Tent,
    'grip-vertical': GripVertical,
    'more-horizontal': MoreHorizontal,
    'panel-left': PanelLeft,
    'menu': Menu,
    'hamburger': Menu,
    'google': GoogleIcon,
    'link': LinkIcon,

    // Weather
    'sun': Sun,
    'cloud': Cloud,
    'cloud-rain': CloudRain,
    'cloud-snow': CloudSnow,
    'cloud-lightning': CloudLightning,
    'cloud-drizzle': CloudDrizzle,
    'cloud-fog': CloudFog,
    'weather-clear': Sun,
    'weather-partly-cloudy': Cloud,
    'weather-overcast': Cloud,
    'weather-fog': CloudFog,
    'weather-drizzle': CloudDrizzle,
    'weather-rain': CloudRain,
    'weather-snow': CloudSnow,
    'weather-thunderstorm': CloudLightning,

    // Location & Maps
    'map-pin': MapPin,
    'map': Map,
    'navigation': Navigation,
    'location': MapPin,

    // Actions & Status
    'edit': Edit,
    'trash': Trash2,
    'delete': Trash2,
    'eraser': Eraser,
    'clear': Eraser,
    'save': Save,
    'download': Download,
    'upload': Upload,
    'share': Share,
    'copy': Copy,
    'eye': Eye,
    'eye-off': EyeOff,
    'alert': AlertTriangle,
    'alert-circle': AlertCircle,
    'check-circle': CheckCircle,
    'x-circle': XCircle,
    'info': Info,
    'help': HelpCircle,
    'help-circle': HelpCircle,
    'life-buoy': LifeBuoy,
    'external-link': ExternalLink,
    'arrow-right': ArrowRight,
    'arrow-left': ArrowLeft,
    'refresh': RotateCcw,
    'reload': RotateCcw,
    'lock-keyhole': LockKeyhole,
    'lock': LockKeyhole,
    'shield': Shield,
    'ruler': Ruler,
    'distance': Ruler,
    'measure': Ruler,
    'bookmark': Bookmark,

    // Communication
    'phone': Phone,
    'mail': Mail,
    'email': Mail,
    'send': Send,

    // Media & Files
    'image': Image,
    'file': File,
    'folder': Folder,
    'folder-open': FolderOpen,
    'file-text': FileText,
    'file-image': FileImage,

    // Social & Family
    'heart': Heart,
    'star': Star,
    'thumbs-up': ThumbsUp,
    'thumbs-down': ThumbsDown,
    'baby': Baby,
    'work': Briefcase,
    'briefcase': Briefcase,
    'user-plus': UserPlus,
    'user-check': UserCheck,
    'user-x': UserX,
    'circle-user': CircleUser,
    
    // Finance
    'dollar-sign': DollarSign,
    'piggy-bank': PiggyBank,

    // Activities & Categories
    'book': BookOpen,
    'art': Palette,
    'music': Music,
    'camera': Camera,
    'gaming': Gamepad2,
    'sports': Dumbbell,
    'nature': TreePine,
    'adventure': Mountain,
    'water': Waves,
    'transport': Car,
    'car': Car,
    'plane': Plane,
    'train': Train,
    'school': School,
    'education': GraduationCap,
    'lightbulb': Lightbulb,
    'zap': Zap,
    'rocket': Rocket,
    'quote': Quote,
    'message-square': MessageSquare,
    'contacts': Contact,
    'gift': Gift,
    'brain': Brain,
    'atom': Atom,
    'activity': Activity,

    // System & Tech
    'wifi': Wifi,
    'wifi-off': WifiOff,
    'battery': Battery,
    'battery-low': BatteryLow,
    'volume': Volume2,
    'volume-off': VolumeX,
    'monitor': Monitor,
    'smartphone': Smartphone,
    'tablet': Tablet,
    'laptop': Laptop,
    'headphones': Headphones,

    // Loading & Spinners
    'loader': LoaderCircle,
    'loader-circle': LoaderCircle,
    'loading': LoaderCircle,
    'spinner': LoaderCircle,

    // Default fallback
    'default': Circle,
    'unknown': HelpCircle
  };

  // Get the icon component
  const IconComponent = iconMap[name] || iconMap['unknown'];
  
  // Build className
  const sizeClass = sizeClasses[size] || sizeClasses['md'];
  const colorClass = colorClasses[color] || '';
  const finalClassName = `${sizeClass} ${colorClass} ${className}`.trim();

  // Handle tooltip
  const tooltipProps = showTooltip ? {
    title: tooltip || name,
    ...props
  } : props;

  return (
    <IconComponent 
      className={finalClassName}
      {...tooltipProps}
    />
  );
};

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  className: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info', 'muted', 'slate', 'white', 'black']),
  showTooltip: PropTypes.bool,
  tooltip: PropTypes.string
};

/**
 * Weather Icon Component
 * Specialized component for weather icons with condition mapping
 */
export const WeatherIcon = ({ 
  condition, 
  size = 'sm', 
  className = '',
  color = '',
  showTooltip = false 
}) => {
  const conditionMap = {
    'clear': 'weather-clear',
    'mostly-clear': 'weather-clear',
    'partly-cloudy': 'weather-partly-cloudy',
    'overcast': 'weather-overcast',
    'fog': 'weather-fog',
    'drizzle': 'weather-drizzle',
    'freezing-drizzle': 'weather-drizzle',
    'rain': 'weather-rain',
    'freezing-rain': 'weather-rain',
    'snow': 'weather-snow',
    'snow-showers': 'weather-snow',
    'rain-showers': 'weather-rain',
    'thunderstorm': 'weather-thunderstorm',
    'unknown': 'unknown'
  };

  const iconName = conditionMap[condition] || 'unknown';

  return (
    <Icon
      name={iconName}
      size={size}
      className={className}
      color={color}
      showTooltip={showTooltip}
      tooltip={condition}
    />
  );
};

WeatherIcon.propTypes = {
  condition: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  className: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info', 'muted', 'slate', 'white', 'black']),
  showTooltip: PropTypes.bool
};

/**
 * Family Avatar Icon Component
 * Specialized component for family member avatars
 */
export const FamilyAvatar = ({ 
  type = 'user', 
  size = 'md', 
  className = '',
  color = 'primary' 
}) => {
  const avatarMap = {
    'user': 'user',
    'family': 'users',
    'child': 'baby',
    'baby': 'baby',
    'all': 'users'
  };

  const iconName = avatarMap[type] || 'user';

  return (
    <Icon
      name={iconName}
      size={size}
      className={className}
      color={color}
    />
  );
};

FamilyAvatar.propTypes = {
  type: PropTypes.oneOf(['user', 'family', 'child', 'baby', 'all']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  className: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info', 'muted', 'slate', 'white', 'black'])
};

/**
 * Status Icon Component
 * Specialized component for status indicators
 */
export const StatusIcon = ({ 
  status = 'info', 
  size = 'sm', 
  className = '',
  showTooltip = true 
}) => {
  const statusMap = {
    'success': 'check-circle',
    'error': 'x-circle',
    'warning': 'alert-triangle',
    'info': 'info',
    'loading': 'loader-circle',
    'pending': 'clock'
  };

  const colorMap = {
    'success': 'success',
    'error': 'error',
    'warning': 'warning',
    'info': 'info',
    'loading': 'muted',
    'pending': 'secondary'
  };

  const iconName = statusMap[status] || 'info';
  const iconColor = colorMap[status] || 'info';

  return (
    <Icon
      name={iconName}
      size={size}
      className={className}
      color={iconColor}
      showTooltip={showTooltip}
      tooltip={status}
    />
  );
};

StatusIcon.propTypes = {
  status: PropTypes.oneOf(['success', 'error', 'warning', 'info', 'loading', 'pending']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  className: PropTypes.string,
  showTooltip: PropTypes.bool
};

export default Icon;
